import React, { useEffect, useState, useRef, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { createCheckIn } from '@/lib/checkin';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Navigation, Trophy, CheckCircle, XCircle, Award, Swords, Clock, AlertCircle, Shield, Crown, Route, Video, Eye, Upload, Loader, BookOpen } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import ScoreSubmissionForm from './ScoreSubmissionForm';
import DuelStartedDialog from './DuelStartedDialog';
import DuelActionButtons from './DuelActionButtons';
import TimeSlotSelector from './TimeSlotSelector';
import ChallengeActionFeedback from './ChallengeActionFeedback';
import DuelNotificationDialog from './DuelNotificationDialog';
import ShareCard from './ShareCard';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';

// Parse les timestamps Supabase (TIMESTAMP sans timezone) en UTC
function parseUTC(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr);
  return new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
}

export default function PlaygroundDetailContent({ playground, showRoute, setShowRoute, routeLoading, onRouteToggle, userLocation }) {
  const { user } = useAuth();
  const { getLocation } = useOptimizedGeolocation();
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const formatCountdown = (totalSec) => {
    if (totalSec <= 0) return '0s';
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
    if (m >= 10) return `${m}min ${s.toString().padStart(2, '0')}s`;
    if (m > 0) return `${m}:${s.toString().padStart(2, '0')}`;
    return `${s}s`;
  };
  const ensureUTC = (d) => {
    if (!d) return d;
    if (typeof d === 'string' && !d.endsWith('Z') && !d.includes('+')) return d + 'Z';
    return d;
  };

  const timeAgoShort = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(ensureUTC(dateStr)).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    return `il y a ${days}j`;
  };

  const formatCourtEvent = (evt) => {
    const name = evt.user_name ? `@${evt.user_name}` : 'Un joueur';
    const opponent = evt.opponent_name ? `@${evt.opponent_name}` : '';
    switch (evt.event_type) {
      case 'created': return `Créé par ${name}`;
      case 'captured': return `${name} a pris le terrain${opponent ? ` à ${opponent}` : ''}`;
      case 'defended': return `Défense réussie par ${name}${opponent ? ` contre ${opponent}` : ''}`;
      case 'fallen_no_defense': return `${name} n'a pas défendu — terrain perdu`;
      case 'record': return `Record atteint par ${name}`;
      default: return `Événement — ${name}`;
    }
  };

  const [duelStartedDialog, setDuelStartedDialog] = useState({ open: false, defenderName: '', playgroundName: '' });
  const [actionFeedback, setActionFeedback] = useState({ show: false, message: '' });
  const [notification, setNotification] = useState({ open: false, type: 'success', message: '', title: '' });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [uploadingHighlight, setUploadingHighlight] = useState(false);
  const [processingHighlightId, setProcessingHighlightId] = useState(null);
  const viewedHighlightsRef = useRef(new Set());
  const queryClient = useQueryClient();

  const { data: ownerUser } = useQuery({
    queryKey: ['ownerUser', playground?.owner_user_id],
    queryFn: async () => {
      if (!playground?.owner_user_id) return null;
      try {
        const { data: userInfo } = await supabase.from('users').select('*').eq('id', playground.owner_user_id).single();
        return userInfo || null;
      } catch (error) {
        console.error('Error fetching owner user:', error);
        return null;
      }
    },
    enabled: !!playground?.owner_user_id,
    retry: false
  });

  // Court history events
  const { data: courtHistory } = useQuery({
    queryKey: ['courtHistory', playground?.id],
    queryFn: async () => {
      const events = await base44.entities.CourtEvent.filter({
        playground_id: playground.id
      });
      const allowed = ['created', 'captured', 'defended', 'fallen_no_defense', 'record'];
      return (events || [])
        .filter(e => allowed.includes(e.event_type))
        .sort((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())
        .slice(0, 5);
    },
    enabled: !!playground?.id,
    staleTime: 30000
  });

  const { data: activeDuel, refetch: refetchDuel } = useQuery({
    queryKey: ['duel', playground?.id],
    queryFn: async () => {
      const duels = await base44.entities.CourtDuel.filter({
        playground_id: playground.id
      });
      const latestDuel = duels?.find((d) => !['COMPLETED', 'CANCELLED'].includes(d.status)) || null;

      if (latestDuel) {
        if (latestDuel.status === 'PENDING_ACCEPT' && new Date() > parseUTC(latestDuel.accept_deadline_at)) {
          try {
            await supabase.from('court_duels').update({ status: 'OPEN', resolution_reason: 'TIMEOUT', open_expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString() }).eq('id', latestDuel.id);
            return new Promise((resolve) => {
              setTimeout(() => {
                base44.entities.CourtDuel.filter({ playground_id: playground.id }).then((d) => resolve(d?.[0] || null));
              }, 500);
            });
          } catch (err) {
            console.error('Failed to resolve duel:', err);
          }
        }

        // PENDING_SLOT_SELECTION expiré → OPEN
        if (latestDuel.status === 'PENDING_SLOT_SELECTION' && (!latestDuel.slot_selection_deadline_at || new Date() > parseUTC(latestDuel.slot_selection_deadline_at))) {
          try {
            await supabase.from('court_duels').update({ status: 'OPEN', resolution_reason: 'SLOT_SELECTION_TIMEOUT', open_expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString() }).eq('id', latestDuel.id);
            return new Promise((resolve) => {
              setTimeout(() => {
                base44.entities.CourtDuel.filter({ playground_id: playground.id }).then((d) => resolve(d?.[0] || null));
              }, 500);
            });
          } catch (err) {
            console.error('Failed to resolve slot selection timeout:', err);
          }
        }
      }

      return latestDuel;
    },
    enabled: !!playground?.id,
    refetchInterval: 3000
  });

  const { data: userXPGain } = useQuery({
    queryKey: ['xpGain', activeDuel?.id, user?.id],
    queryFn: async () => {
      if (!activeDuel?.id || !user?.id) return null;
      const events = await base44.entities.XPEvents.filter({
        duel_id: activeDuel.id,
        user_id: user.id
      });
      const totalXP = events.reduce((sum, e) => sum + e.amount, 0);
      return totalXP > 0 ? totalXP : null;
    },
    enabled: !!activeDuel?.id && !!user?.id && activeDuel?.status === 'COMPLETED'
  });

  // Fetch last completed duel
  const { data: lastCompletedDuel } = useQuery({
    queryKey: ['lastCompletedDuel', playground?.id],
    queryFn: async () => {
      const duels = await base44.entities.CourtDuel.filter({
        playground_id: playground.id,
        status: 'COMPLETED'
      });
      return duels?.sort((a, b) => new Date(b.finished_at || b.updated_date) - new Date(a.finished_at || a.updated_date))?.[0] || null;
    },
    enabled: !!playground?.id
  });

  // Fetch court highlights
  const { data: courtHighlights, refetch: refetchHighlights } = useQuery({
    queryKey: ['courtHighlights', playground?.id],
    queryFn: async () => {
      const highlights = await base44.entities.CourtHighlight.filter({
        playground_id: playground.id,
        visibility_scope: 'COURT',
        mux_status: 'READY'
      });
      return (highlights || [])
        .filter(h => !h.is_deleted)
        .sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
    },
    enabled: !!playground?.id,
    refetchInterval: 10000
  });

  // Check if user can post highlight
  const { data: canPostHighlight } = useQuery({
    queryKey: ['canPostHighlight', lastCompletedDuel?.id, user?.id],
    queryFn: async () => {
      if (!lastCompletedDuel?.id) return { allowed: false };
      try {
        return { allowed: true };
      } catch (error) {
        return { allowed: false };
      }
    },
    enabled: !!lastCompletedDuel?.id && !!user?.id && lastCompletedDuel?.winner_user_id === user?.id
  });



  useEffect(() => {
    if (!activeDuel) {
      setRemainingSeconds(0);
      return;
    }

    let deadlineField;
    if (activeDuel.status === 'PENDING_ACCEPT') {
      deadlineField = activeDuel.accept_deadline_at;
    } else if (activeDuel.status === 'ACTIVE') {
      deadlineField = activeDuel.match_end_at;
    } else {
      setRemainingSeconds(0);
      return;
    }

    const updateTimer = () => {
      const deadline = parseUTC(deadlineField);
      const now = new Date();
      const diff = Math.floor((deadline - now) / 1000);
      setRemainingSeconds(Math.max(0, diff));

      if (diff <= 0) {
        refetchDuel();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeDuel, refetchDuel]);

  // Cooldown timer effect
  useEffect(() => {
    if (playground?.status !== 'LOCKED' || !playground?.locked_until) {
      setCooldownSeconds(0);
      return;
    }

    const rawDate = playground.locked_until;
    const lockEnd = new Date(rawDate.endsWith('Z') || rawDate.includes('+') ? rawDate : rawDate + 'Z');
    const now = new Date();
    const initialDiff = Math.floor((lockEnd - now) / 1000);

    // Unlock expired court on the server, then refetch
    const unlockExpiredCourt = async () => {
      try {
        await supabase.from('playgrounds').update({ status: 'OWNED', locked_until: null }).eq('id', playground.id);
      } catch (e) {
        console.error('Failed to unlock expired court:', e);
      }
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
    };

    // If cooldown already expired, unlock immediately
    if (initialDiff <= 0) {
      setCooldownSeconds(0);
      unlockExpiredCourt();
      return;
    }

    let unlocked = false;
    const updateCooldownTimer = () => {
      const raw = playground.locked_until;
      const lockEnd = new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z');
      const now = new Date();
      const diff = Math.floor((lockEnd - now) / 1000);
      setCooldownSeconds(Math.max(0, diff));

      if (diff <= 0 && !unlocked) {
        unlocked = true;
        unlockExpiredCourt();
      }
    };

    updateCooldownTimer();
    const interval = setInterval(updateCooldownTimer, 1000);
    return () => clearInterval(interval);
  }, [playground?.status, playground?.locked_until, playground?.id, queryClient]);

    const checkInMutation = useMutation({
    mutationFn: async () => {
      const location = await getLocation({ 
        useCache: false,
        highAccuracy: true, 
        timeout: 5000 
      });

      const data = await createCheckIn({
        playground_id: playground.id,
        user_lat: location.lat,
        user_lng: location.lng
      });
      return data;
    },
    onSuccess: (data) => {
      setLastCheckIn(data);
      if (data.is_valid) {
        queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
        queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      }
    }
  });

  const duelMutation = useMutation({
    mutationFn: async () => {
      // Check-in + startDuel en une seule action
      const location = await getLocation({ useCache: false, highAccuracy: true, timeout: 5000 });
      const checkinData = await createCheckIn({ playground_id: playground.id, user_lat: location.lat, user_lng: location.lng });
      if (!checkinData.is_valid) throw new Error(`Trop loin ! Distance: ${Math.round(checkinData.distance_m)}m. Rapproche-toi du terrain.`);
      setLastCheckIn(checkinData);
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/startDuel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ playground_id: playground.id, checkin_id: checkinData.checkin_id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors du lancement du duel');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      setDuelStartedDialog({
        open: true,
        defenderName: activeDuel?.defender_name || data.defender_name || 'Opponent',
        playgroundName: playground?.name || 'Playground'
      });
      setLastCheckIn(null);
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec du lancement du duel'
      });
    }
  });

  const submitScoreMutation = useMutation({
    mutationFn: async (scores) => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submitDuelScore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: activeDuel.id, attacker_score: scores.attacker_score, defender_score: scores.defender_score })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la soumission du score');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      setNotification({
        open: true,
        type: 'success',
        title: 'Score soumis',
        message: data.message || 'En attente de confirmation de l\'adversaire'
      });
      setLastCheckIn(null);
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de la soumission du score'
      });
    }
  });

  const confirmScoreMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/confirmDuelScore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: activeDuel.id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la confirmation');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['ownerUser'] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      queryClient.invalidateQueries({ queryKey: ['xpHistory'] });
      queryClient.invalidateQueries({ queryKey: ['userProgress'] });
      queryClient.invalidateQueries({ queryKey: ['userTickets'] });
      queryClient.invalidateQueries({ queryKey: ['myTerritories'] });
      queryClient.invalidateQueries({ queryKey: ['userRecentVictories'] });
      setNotification({
        open: true,
        type: 'victory',
        title: 'Duel terminé',
        message: data.message || 'Le match est terminé !'
      });
      setLastCheckIn(null);
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de la confirmation du score'
      });
    }
  });

  const disputeScoreMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/disputeScore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: activeDuel.id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la contestation');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      setNotification({
        open: true,
        type: 'info',
        title: 'Score contesté',
        message: data.message || 'L\'adversaire doit resoumettre le score.'
      });
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de la contestation'
      });
    }
  });

  const resubmitScoreMutation = useMutation({
    mutationFn: async (scores) => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { error } = await supabase.from('court_duels').update({
        status: 'SCORE_SUBMITTED',
        attacker_score: scores.attacker_score,
        defender_score: scores.defender_score,
        score_submitted_by_user_id: authUser.id,
        score_submitted_at: new Date().toISOString(),
        score_deadline_at: new Date(Date.now() + 15 * 60 * 1000).toISOString()
      }).eq('id', activeDuel.id);
      if (error) throw error;
      const response = { data: { success: true } };
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      setNotification({
        open: true,
        type: 'success',
        title: 'Score resoumis',
        message: data.message || 'En attente de confirmation de l\'adversaire'
      });
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de la resoumission'
      });
    }
  });

  const abandonMatchMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/abandonMatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: activeDuel.id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de l\'abandon');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      setNotification({
        open: true,
        type: 'info',
        title: 'Match abandonné',
        message: data.message || 'L\'adversaire gagne par forfait.'
      });
      setLastCheckIn(null);
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de l\'abandon'
      });
    }
  });

  const captureMutation = useMutation({
    mutationFn: async () => {
      // Check-in + capture en une seule action
      const location = await getLocation({ useCache: false, highAccuracy: true, timeout: 5000 });
      const checkinData = await createCheckIn({ playground_id: playground.id, user_lat: location.lat, user_lng: location.lng });
      if (!checkinData.is_valid) throw new Error(`Trop loin ! Distance: ${Math.round(checkinData.distance_m)}m. Rapproche-toi du terrain.`);
      setLastCheckIn(checkinData);
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/capturePlayground`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ playground_id: playground.id, checkin_id: checkinData.checkin_id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la capture');
      return result;
    },
    onMutate: async () => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['playground', playground.id] });
      await queryClient.cancelQueries({ queryKey: ['playgrounds'] });
      await queryClient.cancelQueries({ queryKey: ['myTerritories'] });

      // Snapshot current cache for rollback
      const previousPlayground = queryClient.getQueryData(['playground', playground.id]);
      const previousPlaygrounds = queryClient.getQueryData(['playgrounds']);
      const previousMyTerritories = queryClient.getQueryData(['myTerritories']);

      // Optimistically update ['playground', id]
      queryClient.setQueryData(['playground', playground.id], (old) => {
        if (!old) return old;
        return {
          ...old,
          owner_user_id: user.id,
          status: 'OWNED'
        };
      });

      // Optimistically update ['playgrounds'] list
      queryClient.setQueryData(['playgrounds'], (old) => {
        if (!Array.isArray(old)) return old;
        return old.map(pg =>
          pg.id === playground.id
            ? { ...pg, owner_user_id: user.id, status: 'OWNED' }
            : pg
        );
      });

      // Optimistically add to ['myTerritories'] if it exists
      queryClient.setQueryData(['myTerritories'], (old) => {
        if (!Array.isArray(old)) return old;
        // Avoid duplicates
        if (old.some(t => t.id === playground.id)) return old;
        return [...old, { ...playground, owner_user_id: user.id, status: 'OWNED' }];
      });

      // Return context for rollback
      return { previousPlayground, previousPlaygrounds, previousMyTerritories };
    },
    onError: (error, _variables, context) => {
      // Rollback on error using snapshots
      if (context?.previousPlayground !== undefined) {
        queryClient.setQueryData(['playground', playground.id], context.previousPlayground);
      }
      if (context?.previousPlaygrounds !== undefined) {
        queryClient.setQueryData(['playgrounds'], context.previousPlaygrounds);
      }
      if (context?.previousMyTerritories !== undefined) {
        queryClient.setQueryData(['myTerritories'], context.previousMyTerritories);
      }

      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de la capture du terrain'
      });
    },
    onSuccess: (data) => {
      // Invalidate to sync with server (won't cause flicker since data already matches)
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['ownerUser', playground?.owner_user_id] });
      queryClient.invalidateQueries({ queryKey: ['ownerUser', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      queryClient.invalidateQueries({ queryKey: ['myTerritories'] });
      queryClient.invalidateQueries({ queryKey: ['xpHistory'] });
      queryClient.invalidateQueries({ queryKey: ['userProgress'] });
      queryClient.invalidateQueries({ queryKey: ['userTickets'] });

      // Show feedback if there's an active add_court challenge
      const addCourtChallenge = activeChallenges?.find((c) => c.condition_type === 'add_court');
      if (addCourtChallenge) {
        setActionFeedback({
          show: true,
          message: `🔥 Bien joué ! +${addCourtChallenge.reward_value} ${addCourtChallenge.reward_value === 1 ? 'ticket' : 'tickets'} pour le tirage`
        });
      }

      setNotification({
        open: true,
        type: 'success',
        title: 'Terrain capturé',
        message: data.message || 'Vous êtes maintenant propriétaire du terrain !'
      });
      setLastCheckIn(null);
    }
  });

  const cancelCooldownMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('playgrounds').update({ status: 'FREE', locked_until: null }).eq('id', playground.id);
      if (error) throw error;
      return { success: true };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['playground', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      setNotification({
        open: true,
        type: 'success',
        title: 'Cooldown annulé',
        message: data.message || 'Le terrain est à nouveau disponible'
      });
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Échec de l\'annulation du cooldown'
      });
    }
  });

  const startScheduledMatchMutation = useMutation({
    mutationFn: async () => {
      // Get fresh location + check-in
      const location = await getLocation({ useCache: true, timeout: 5000 });
      const checkinData = await createCheckIn({
        playground_id: playground.id,
        user_lat: location.lat,
        user_lng: location.lng
      });
      if (!checkinData.is_valid) {
        throw new Error(`Trop loin ! Distance: ${checkinData.distance_m}m. Vous devez être au terrain.`);
      }
      const { error } = await supabase.from('court_duels').update({
        status: 'ACTIVE',
        match_end_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString()
      }).eq('id', activeDuel.id);
      if (error) throw error;
      const response = { data: { success: true } };
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      setNotification({
        open: true,
        type: 'success',
        title: 'Match démarré !',
        message: data.message || 'Le match est maintenant actif. Soumettez le score avant la fin.'
      });
    },
    onError: (error) => {
      setNotification({
        open: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.error || error.message || 'Impossible de démarrer le match.'
      });
    }
  });

  const handlePostHighlight = async () => {
    if (!lastCompletedDuel?.id) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file size (100 MB max)
      if (file.size > 100 * 1024 * 1024) {
        setNotification({
          open: true,
          type: 'error',
          title: 'Fichier trop volumineux',
          message: 'La vidéo ne doit pas dépasser 100 Mo'
        });
        return;
      }

      // Validate duration (30s max)
      try {
        const duration = await new Promise((resolve, reject) => {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            resolve(video.duration);
          };
          video.onerror = () => {
            URL.revokeObjectURL(video.src);
            reject(new Error('Cannot read video'));
          };
          video.src = URL.createObjectURL(file);
        });
        if (duration > 30) {
          setNotification({
            open: true,
            type: 'error',
            title: 'Vidéo trop longue',
            message: 'Le highlight ne doit pas dépasser 30 secondes'
          });
          return;
        }
      } catch {
        // If we can't read duration, let the backend reject it
      }

      setUploadingHighlight(true);
      try {
        // Create Mux upload
        console.warn('[Migration] createMuxDirectUploadForHighlight not migrated yet');
        const createResponse = { data: null };

        const { upload_url, highlight_id } = createResponse.data;

        // Upload file to Mux
        const uploadResponse = await fetch(upload_url, {
          method: 'PUT',
          body: file
        });

        if (!uploadResponse.ok) {
          throw new Error('Upload failed');
        }

        setNotification({
          open: true,
          type: 'success',
          title: 'Highlight en cours de traitement',
          message: 'Votre vidéo sera disponible dans quelques instants'
        });

        // Show processing placeholder
        setProcessingHighlightId(highlight_id);

        // Polling fallback: check status every 4s for up to 90s
        let pollCount = 0;
        const maxPolls = 22; // ~90s
        const pollInterval = setInterval(async () => {
          pollCount++;
          try {
            console.warn('[Migration] refreshMuxHighlightStatus not migrated yet');
            const res = { data: { status: 'READY' } };
            if (res.data?.mux_status === 'READY' || res.data?.mux_status === 'ERRORED' || pollCount >= maxPolls) {
              clearInterval(pollInterval);
              setProcessingHighlightId(null);
              refetchHighlights();
              queryClient.invalidateQueries({ queryKey: ['canPostHighlight'] });
            }
          } catch (pollErr) {
            console.error('Highlight poll error:', pollErr);
            if (pollCount >= maxPolls) {
              clearInterval(pollInterval);
              setProcessingHighlightId(null);
            }
          }
        }, 4000);

      } catch (error) {
        setNotification({
          open: true,
          type: 'error',
          title: 'Erreur',
          message: error.response?.data?.error || error.message || 'Échec de l\'upload'
        });
      } finally {
        setUploadingHighlight(false);
      }
    };
    input.click();
  };

  const viewTimerRef = useRef(null);

  const handleHighlightClick = (highlight) => {
    setSelectedHighlight(highlight);
    // Start 3s timer for view count
    if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    viewTimerRef.current = setTimeout(async () => {
      viewTimerRef.current = null;
      try {
        await supabase.rpc('increment_highlight_views', { h_id: highlight.id }).catch(() =>
          supabase.from('court_highlights').update({ views_count: (highlight.views_count || 0) + 1 }).eq('id', highlight.id)
        );
        refetchHighlights();
      } catch (error) {
        console.error('Failed to increment view:', error);
      }
    }, 3000);
  };

  const handleCloseHighlight = () => {
    // Cancel view count if closed before 3s
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
      viewTimerRef.current = null;
    }
    setSelectedHighlight(null);
  };

  return (
    <>
            <ChallengeActionFeedback
        message={actionFeedback.message}
        show={actionFeedback.show}
        onDismiss={() => setActionFeedback({ show: false, message: '' })} />

            <DuelStartedDialog
        open={duelStartedDialog.open}
        onOpenChange={(open) => setDuelStartedDialog({ ...duelStartedDialog, open })}
        defenderName={duelStartedDialog.defenderName}
        playgroundName={duelStartedDialog.playgroundName} />

            <DuelNotificationDialog
        open={notification.open}
        onClose={() => setNotification({ ...notification, open: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message} />

            <div className="space-y-4">
            {/* Owner + Photos Card */}
            <div className="border-l-4 border-[#6E2BFF] bg-[#13151D] p-5 space-y-2">
                <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold mb-2">Propriétaire actuel</p>
                {playground?.owner_user_id ?
                  <div className="flex items-center justify-between">
                      <div>
                          <p className="text-white text-base font-black uppercase tracking-tight">{ownerUser?.username || ownerUser?.full_name || 'Inconnu'}</p>
                          {ownerUser &&
                            <p className="text-xs text-[#B3B6C2] mt-1">Propriétaire – Niveau {ownerUser.level || 1}</p>
                          }
                      </div>
                      {user?.id === playground.owner_user_id &&
                        <Badge className="bg-[#6E2BFF] text-white text-xs font-bold">VOUS</Badge>
                      }
                  </div> :
                  <p className="text-[#B3B6C2] italic text-sm font-medium">PLAYGROUND NON RÉCLAMÉ</p>
                }

                {(playground.photo_urls || playground.photo_url) &&
          <div className="pt-3 border-t border-[#1B1E28] mt-3">
                        <p className="text-xs uppercase tracking-wider text-[#B3B6C2] font-semibold mb-3">Photos du terrain</p>
                        <div className="grid grid-cols-2 gap-2">
                            {playground.photo_urls?.map((url, idx) =>
              <img
                key={idx}
                src={url}
                alt={`Photo ${idx + 1}`}
                className="w-full h-20 object-cover rounded border border-[#6E2BFF]/30 hover:border-[#6E2BFF] transition-colors cursor-pointer"
                onClick={() => setSelectedPhoto(url)} />

              )}
                            {playground.photo_url &&
              <img
                src={playground.photo_url}
                alt="Terrain photo"
                className="w-full h-20 object-cover rounded border border-[#6E2BFF]/30 hover:border-[#6E2BFF] transition-colors cursor-pointer"
                onClick={() => setSelectedPhoto(playground.photo_url)} />

              }
                        </div>
                    </div>
          }
            </div>

            {/* Court History */}
            {courtHistory && courtHistory.length > 0 && (
              <div className="border-l-4 border-[#6E2BFF] bg-[#1B1E28] p-5">
                <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold mb-3">Historique</p>
                <div className="space-y-2 max-h-[140px] overflow-y-auto">
                  {courtHistory.map((evt, i) => (
                    <div key={evt.id || i} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#6E2BFF] mt-1.5 flex-shrink-0" />
                      <p className="text-xs text-[#B3B6C2] leading-relaxed">
                        {formatCourtEvent(evt)}
                        <span className="text-[#9CA0AB] ml-1">— {timeAgoShort(evt.created_date)}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cooldown Status - Shown for everyone */}
            {playground.status === 'LOCKED' && !activeDuel && (
              <div className="border-l-4 border-amber-600 bg-gradient-to-br from-amber-900/20 to-amber-800/10 p-5 space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <p className="text-sm font-black uppercase tracking-wide text-amber-500">Terrain en cooldown</p>
                </div>
                <p className="text-[#B3B6C2] text-sm">
                  Un duel s'est terminé récemment sur ce terrain. Il sera à nouveau disponible pour un challenge dans :
                </p>
                <div className="bg-[#0A0B10] rounded-lg p-4 border border-amber-600/30 text-center">
                  <p className="text-4xl font-black text-amber-500 tracking-tight mb-2">
                    {formatCountdown(cooldownSeconds)}
                  </p>
                  <p className="text-xs text-[#B3B6C2] uppercase tracking-wider">
                    Temps restant
                  </p>
                </div>
                {playground.owner_user_id === user?.id && (
                  <p className="text-xs text-amber-400 text-center mt-2">
                    ⭐ En tant que propriétaire, vous pouvez annuler le cooldown ci-dessous
                  </p>
                )}
              </div>
            )}

            {/* Duel Status - Most Expressive */}
            {activeDuel &&
        <div className="border-l-4 border-[#6E2BFF] bg-[#13151D] p-5 space-y-4">
                    {/* Status Label */}
                    <div className="text-center">
                        <Badge className="bg-[#6E2BFF] text-white text-xs font-bold uppercase tracking-wider px-4 py-1">
                            {activeDuel.status === 'PENDING_ACCEPT' ? '⚠️ Challenge en attente' :
              activeDuel.status === 'PENDING_SLOT_SELECTION' ? '📅 Choix du créneau' :
              activeDuel.status === 'SCHEDULED' ? '🗓️ Match programmé' :
              activeDuel.status === 'OPEN' ? '🔓 Duel ouvert' :
              activeDuel.status === 'ACTIVE' ? '⚡ Match actif' :
              activeDuel.status === 'SCORE_SUBMITTED' ? '📊 En attente de confirmation' :
              activeDuel.status === 'SCORE_DISPUTED' ? '❌ Score contesté' :
              activeDuel.status === 'COMPLETED' ? '👑 Match terminé' :
              '❓ Statut inconnu'}
                        </Badge>
                    </div>

                    {/* Player Cards */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Attacker Card */}
                        <div className="bg-[#1B1E28] p-3 rounded-lg border border-[#6E2BFF]/30">
                            <div className="flex items-center gap-2 mb-2">
                                <Swords className="w-4 h-4 text-red-500" />
                                <span className="text-xs uppercase tracking-wider text-[#B3B6C2] font-bold">Attaquant</span>
                            </div>
                            <p className="text-white font-bold truncate">{activeDuel.attacker_name}</p>
                            {user?.id === activeDuel.attacker_user_id &&
                    <Badge className="bg-red-500 text-white text-xs mt-2">VOUS</Badge>
                    }
                            {activeDuel.status === 'COMPLETED' && activeDuel.attacker_score !== null &&
                    <p className="text-2xl font-black text-[#6E2BFF] mt-2">{activeDuel.attacker_score}</p>
                    }
                        </div>

                        {/* Defender Card */}
                        <div className="bg-[#1B1E28] p-3 rounded-lg border border-[#6E2BFF]/30">
                            <div className="flex items-center gap-2 mb-2">
                                <Shield className="w-4 h-4 text-blue-500" />
                                <span className="text-xs uppercase tracking-wider text-[#B3B6C2] font-bold">Défenseur</span>
                            </div>
                            <p className="text-white font-bold truncate">{activeDuel.defender_name}</p>
                            {user?.id === activeDuel.defender_user_id &&
                    <Badge className="bg-blue-500 text-white text-xs mt-2">VOUS</Badge>
                    }
                            {activeDuel.status === 'COMPLETED' && activeDuel.defender_score !== null &&
                    <p className="text-2xl font-black text-[#6E2BFF] mt-2">{activeDuel.defender_score}</p>
                    }
                        </div>
                    </div>

                    {/* Timer for PENDING_ACCEPT or ACTIVE */}
                    {['PENDING_ACCEPT', 'ACTIVE'].includes(activeDuel.status) &&
          <div className="text-center py-3 border-t border-[#1B1E28]">
                            <p className="text-4xl font-black text-[#6E2BFF] tracking-tight">{formatCountdown(remainingSeconds)}</p>
                            <p className="text-xs text-[#B3B6C2] uppercase tracking-wider mt-1">
                                {activeDuel.status === 'PENDING_ACCEPT' ? 'Temps pour accepter' : 'Temps restant du match'}
                            </p>
                        </div>
          }

                    {/* Winner Display for COMPLETED */}
                    {activeDuel.status === 'COMPLETED' && activeDuel.winner_user_id &&
          <div className="py-3 border-t border-[#1B1E28] space-y-3">
                            <div className="text-center">
                                <p className="text-lg font-black text-white">
                                    {activeDuel.winner_user_id === activeDuel.attacker_user_id ? activeDuel.attacker_name : activeDuel.defender_name}
                                </p>
                                <p className="text-sm text-[#B3B6C2] mt-1">
                                    {activeDuel.winner_user_id === activeDuel.attacker_user_id ? 'Terrain conquis' : 'Terrain défendu'}
                                </p>
                            </div>

                            <div className="text-center">
                                <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Propriété</p>
                                <p className="text-sm text-white">
                                    {activeDuel.winner_user_id === activeDuel.attacker_user_id ? 'Conquis par l\'attaquant' : 'Reste au défenseur'}
                                </p>
                            </div>

                            {user?.id === activeDuel.winner_user_id && userXPGain !== null ?
            <div className="text-center">
                                    <p className="text-sm font-bold text-[#6E2BFF]">+{userXPGain} XP</p>
                                </div> :
            user?.id && user?.id !== activeDuel.winner_user_id ?
            <div className="text-center">
                                    <p className="text-sm text-[#B3B6C2]">Aucun XP gagné</p>
                                </div> :
            null}
                        </div>
          }
          </div>
          }

          {/* Court Memory Section — only if activated */}
          {playground?.memory_enabled && playground?.memory_data && (() => {
            const memory = typeof playground.memory_data === 'string'
              ? JSON.parse(playground.memory_data)
              : playground.memory_data;
            if (!memory || !memory.total_duels) return null;
            return (
              <div className="border-l-4 border-amber-500 bg-[#13151D] p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-amber-500" />
                    <p className="text-xs uppercase tracking-wider text-[#B3B6C2] font-bold">Mémoire du terrain</p>
                  </div>
                  <ShareCard
                    event_type={memory.top_defender ? 'defended' : 'created'}
                    playground_name={playground?.name}
                    playground_city={playground?.city}
                    user_name={memory.top_defender?.user_name || memory.top_attacker?.user_name}
                    sponsor={memory.sponsor}
                    className="text-amber-500 hover:text-amber-400 p-1"
                  />
                </div>
                {memory.sponsor && (
                  <p className="text-[10px] text-amber-500/60 uppercase tracking-widest">Sponsorisé par {memory.sponsor}</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#1B1E28] rounded-lg p-3 text-center">
                    <p className="text-2xl font-black text-white">{memory.total_duels}</p>
                    <p className="text-[10px] text-[#B3B6C2] uppercase tracking-wider mt-1">Duels joués</p>
                  </div>
                  {memory.top_defender && (
                    <div className="bg-[#1B1E28] rounded-lg p-3 text-center">
                      <Shield className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white truncate">{memory.top_defender.user_name}</p>
                      <p className="text-[10px] text-[#B3B6C2] uppercase tracking-wider mt-1">{memory.top_defender.count}x défenseur</p>
                    </div>
                  )}
                  {memory.top_attacker && (
                    <div className="bg-[#1B1E28] rounded-lg p-3 text-center">
                      <Swords className="w-4 h-4 text-red-400 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white truncate">{memory.top_attacker.user_name}</p>
                      <p className="text-[10px] text-[#B3B6C2] uppercase tracking-wider mt-1">{memory.top_attacker.count}x conquérant</p>
                    </div>
                  )}
                  {memory.biggest_score_gap && (
                    <div className="bg-[#1B1E28] rounded-lg p-3 text-center">
                      <Trophy className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white">{memory.biggest_score_gap.score}</p>
                      <p className="text-[10px] text-[#B3B6C2] uppercase tracking-wider mt-1">Plus gros écart</p>
                    </div>
                  )}
                  {memory.longest_defense_streak && (
                    <div className="bg-[#1B1E28] rounded-lg p-3 text-center">
                      <Crown className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                      <p className="text-sm font-bold text-white">{memory.longest_defense_streak.streak}x</p>
                      <p className="text-[10px] text-[#B3B6C2] uppercase tracking-wider mt-1">Série {memory.longest_defense_streak.user_name}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Highlights Section — always visible */}
          <div className="border-l-4 border-[#6E2BFF] bg-[#13151D] p-5 space-y-4">
          <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-[#6E2BFF]" />
            <p className="text-xs uppercase tracking-wider text-[#B3B6C2] font-bold">Highlights du terrain</p>
          </div>
          {courtHighlights?.length > 0 && (
            <Badge className="bg-[#6E2BFF]/20 text-[#6E2BFF] text-xs">
              {courtHighlights.length}
            </Badge>
          )}
          </div>

          {(courtHighlights && courtHighlights.length > 0) || processingHighlightId ? (
          <div className="grid grid-cols-2 gap-3">
          {processingHighlightId && (
            <div className="relative">
              <div className="relative aspect-video bg-[#1B1E28] rounded-lg overflow-hidden border border-[#6E2BFF]/30 flex items-center justify-center">
                <div className="text-center">
                  <Loader className="w-6 h-6 text-[#6E2BFF] animate-spin mx-auto mb-2" />
                  <p className="text-xs text-[#9CA0AB]">Traitement...</p>
                </div>
              </div>
            </div>
          )}
          {courtHighlights?.map((highlight) => (
            <div
              key={highlight.id}
              className="relative cursor-pointer group"
              onClick={() => handleHighlightClick(highlight)}
            >
              <div className="relative aspect-video bg-[#1B1E28] rounded-lg overflow-hidden border border-[#6E2BFF]/30 group-hover:border-[#6E2BFF] transition-all">
                {highlight.thumbnail_url ? (
                  <img
                    src={highlight.thumbnail_url}
                    alt="Highlight thumbnail"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-8 h-8 text-[#6E2BFF]/50" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-[#6E2BFF] flex items-center justify-center">
                    <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-white border-b-8 border-b-transparent ml-1" />
                  </div>
                </div>
                {highlight.duration_sec && (
                  <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                    {Math.floor(highlight.duration_sec / 60)}:{String(Math.floor(highlight.duration_sec % 60)).padStart(2, '0')}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-1 text-xs text-[#B3B6C2]">
                  <Eye className="w-3 h-3" />
                  <span>{highlight.views_count || 0}</span>
                </div>
                {highlight.created_by_user_id && (
                  <HighlightOwner userId={highlight.created_by_user_id} />
                )}
              </div>
            </div>
          ))}
          </div>
          ) : (
            <p className="text-sm text-[#9CA0AB] text-center py-4">Aucun highlight pour l'instant</p>
          )}
          </div>

          {/* Check-in Result */}
            {lastCheckIn &&
        <div className={`p-4 border-l-4 flex items-start gap-3 ${lastCheckIn.is_valid ? 'bg-[#13151D] border-green-600' : 'bg-[#13151D] border-red-600'}`}>
                    {lastCheckIn.is_valid ?
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /> :

          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          }
                    <div className="text-sm">
                        <p className="font-bold text-white">{lastCheckIn.is_valid ? 'Check-in réussi !' : 'Trop loin'}</p>
                        <p className="text-xs text-[#B3B6C2] mt-1">{lastCheckIn.distance_m}m (max {lastCheckIn.threshold_m}m)</p>
                    </div>
                </div>
        }

            {/* Action Buttons */}
            {user ?
        <div className="space-y-2 pt-2">
                    <Button
            variant="outline"
            className="w-full text-sm border-[#6E2BFF] text-[#6E2BFF] hover:bg-[#6E2BFF]/10 hover:border-[#8B5CFF] transition-all"
            onClick={() => onRouteToggle(playground)}
            disabled={routeLoading || !userLocation}>

                        <Route className="w-4 h-4 mr-2" />
                        {routeLoading ? 'Chargement...' : showRoute ? 'Masquer l\'itinéraire' : 'Afficher l\'itinéraire'}
                    </Button>

                    {playground.status === 'LOCKED' ? (
                      <Button
                        className="w-full bg-amber-600/20 border-2 border-amber-600 text-amber-500 font-bold text-sm transition-all cursor-not-allowed"
                        disabled={true}
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Terrain en cooldown ({formatCountdown(cooldownSeconds)})
                      </Button>
                    ) : playground.status === 'FREE' && !activeDuel ? (
                      <Button
                        className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold text-sm uppercase transition-all shadow-lg hover:shadow-lg hover:shadow-[#6E2BFF]/40"
                        onClick={() => captureMutation.mutate()}
                        disabled={captureMutation.isPending || !navigator.geolocation}
                      >
                        <Trophy className="w-4 h-4 mr-2" />
                        {captureMutation.isPending ? 'Vérification de ta position...' : 'Capturer ce terrain'}
                      </Button>
                    ) : playground.status === 'OWNED' && playground.owner_user_id !== user.id && !activeDuel ? (
                      <Button
                        className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold text-sm uppercase transition-all shadow-lg hover:shadow-lg hover:shadow-[#6E2BFF]/40"
                        onClick={() => duelMutation.mutate()}
                        disabled={duelMutation.isPending || !navigator.geolocation}
                      >
                        <Swords className="w-4 h-4 mr-2" />
                        {duelMutation.isPending ? 'Vérification de ta position...' : 'Défier'}
                      </Button>
                    ) : !activeDuel ? (
                      <Button
                        className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold text-sm transition-all shadow-lg hover:shadow-lg hover:shadow-[#6E2BFF]/40"
                        onClick={() => checkInMutation.mutate()}
                        disabled={checkInMutation.isPending || !navigator.geolocation}
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        {checkInMutation.isPending ? 'Vérification...' : 'Check-in'}
                      </Button>
                    ) : null}

                    {activeDuel && activeDuel.status === 'PENDING_ACCEPT' && activeDuel.defender_user_id === user.id &&
          <DuelActionButtons
            duel={activeDuel}
            playground={playground}
            lastCheckIn={lastCheckIn}
            onSuccess={(data) => setNotification({ open: true, ...data })}
            onError={(data) => setNotification({ open: true, ...data })}
          />
          }

                    {/* Attaquant choisit un créneau */}
                    {activeDuel && activeDuel.status === 'PENDING_SLOT_SELECTION' && activeDuel.attacker_user_id === user.id && (
                      <>
                        <TimeSlotSelector
                          duel={activeDuel}
                          playground={playground}
                          onSuccess={(data) => setNotification({ open: true, ...data })}
                          onError={(data) => setNotification({ open: true, ...data })}
                        />
                      </>
                    )}

                    {/* Scheduled Match - Show date/time */}
                    {activeDuel && activeDuel.status === 'SCHEDULED' && (
                      <Card className="bg-gradient-to-br from-green-900/30 to-green-800/20 border-2 border-green-600 p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-bold text-green-400 uppercase mb-2">
                              Match programmé
                            </p>
                            <div className="bg-[#0A0B10] rounded-lg p-3 border border-green-600/30 mb-3">
                              <p className="text-xs text-[#B3B6C2] mb-1">Date et heure</p>
                              <p className="text-white font-bold text-lg">
                                {activeDuel.scheduled_at && new Date(activeDuel.scheduled_at).toLocaleString('fr-FR', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>

                            {/* Check if match time has arrived and user has checked in */}
                            {lastCheckIn?.is_valid && activeDuel.scheduled_at && parseUTC(activeDuel.scheduled_at) <= new Date() ? (
                              <>
                                <p className="text-xs text-green-400 mb-2 font-bold">
                                  ✓ Il est l'heure ! Vous êtes au terrain.
                                </p>
                                <Button
                                  className="w-full bg-green-600 hover:bg-green-500 text-white font-bold text-sm uppercase"
                                  onClick={() => startScheduledMatchMutation.mutate()}
                                  disabled={startScheduledMatchMutation.isPending}
                                >
                                  {startScheduledMatchMutation.isPending ? 'Démarrage...' : 'Démarrer le match maintenant'}
                                </Button>
                              </>
                            ) : (
                              <p className="text-xs text-[#B3B6C2]">
                                {lastCheckIn?.is_valid
                                  ? 'Attendez l\'heure prévue pour démarrer le match.'
                                  : '⚠️ Faites un check-in au terrain pour pouvoir démarrer le match à l\'heure.'}
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>
                    )}

                    {activeDuel && activeDuel.status === 'ACTIVE' && (() => {
                      const isParticipant = activeDuel.attacker_user_id === user.id || activeDuel.defender_user_id === user.id;
                      if (isParticipant) {
                        return (
                          <>
                            {activeDuel.resolution_reason === 'OPEN_DUEL_PLAYED' && (
                              <p className="text-xs text-amber-400/80 text-center mb-2">
                                Duel ouvert — un nouveau défenseur a rejoint le match.
                              </p>
                            )}
                            <ScoreSubmissionForm
                              duel={activeDuel}
                              user={user}
                              onSubmit={(scores) => submitScoreMutation.mutate(scores)}
                              isLoading={submitScoreMutation.isPending} />
                            <Button
                              variant="ghost"
                              className="w-full text-red-400/60 hover:text-red-400 hover:bg-red-500/10 text-xs mt-2"
                              onClick={() => {
                                if (confirm('Abandonner le match ? L\'adversaire gagnera 21-0 par forfait.')) {
                                  abandonMatchMutation.mutate();
                                }
                              }}
                              disabled={abandonMatchMutation.isPending}>
                              {abandonMatchMutation.isPending ? 'Abandon...' : 'Abandonner le match'}
                            </Button>
                          </>
                        );
                      }
                      if (!isParticipant) {
                        return (
                          <Card className="bg-amber-500/10 border border-amber-500/30 p-4">
                            <p className="text-amber-400 text-sm font-semibold text-center">
                              Un autre joueur a pris la place du défenseur. Tu n'es pas participant.
                            </p>
                            <p className="text-xs text-[#9CA0AB] text-center mt-1">
                              Seuls {activeDuel.attacker_name} et {activeDuel.defender_name} peuvent soumettre le score.
                            </p>
                          </Card>
                        );
                      }
                      return null;
                    })()}

                    {activeDuel && activeDuel.status === 'SCORE_SUBMITTED' && (() => {
                      const isParticipant = activeDuel.attacker_user_id === user.id || activeDuel.defender_user_id === user.id;
                      const isSubmitter = activeDuel.score_submitted_by_user_id === user.id;
                      const deadlineAt = activeDuel.score_deadline_at ? parseUTC(activeDuel.score_deadline_at) : null;
                      const deadlineMs = deadlineAt ? Math.max(0, deadlineAt - new Date()) : null;
                      const deadlineMin = deadlineMs !== null ? Math.ceil(deadlineMs / 60000) : null;

                      if (!isParticipant) {
                        return (
                          <Card className="bg-amber-500/10 border border-amber-500/30 p-4">
                            <p className="text-amber-400 text-sm font-semibold text-center">
                              Ce duel est en cours entre d'autres joueurs. Tu ne peux pas confirmer les scores.
                            </p>
                            <p className="text-xs text-[#9CA0AB] text-center mt-1">
                              Seuls {activeDuel.attacker_name} et {activeDuel.defender_name} sont participants.
                            </p>
                          </Card>
                        );
                      }

                      return (
                        <>
                          <Card className="bg-[#13151D] border border-[#6E2BFF]/30 p-4 space-y-3">
                            <p className="text-xs uppercase tracking-wider text-[#B3B6C2] font-bold text-center">
                              {isSubmitter ? 'Score soumis — en attente' : 'Scores soumis par l\'adversaire'}
                            </p>
                            <div className="grid grid-cols-2 gap-3 text-center">
                              <div>
                                <p className="text-xs text-[#B3B6C2]">{activeDuel.attacker_name}</p>
                                <p className="text-2xl font-black text-white">{activeDuel.attacker_score ?? '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#B3B6C2]">{activeDuel.defender_name}</p>
                                <p className="text-2xl font-black text-white">{activeDuel.defender_score ?? '-'}</p>
                              </div>
                            </div>
                            {deadlineMin !== null && (
                              <p className="text-xs text-center text-amber-400">
                                {deadlineMin > 0
                                  ? `Validation auto dans ${deadlineMin} min si pas de réponse`
                                  : 'Validation automatique imminente'}
                              </p>
                            )}
                          </Card>

                          {isSubmitter ? (
                            <p className="text-xs text-[#9CA0AB] text-center">
                              En attente de confirmation de l'adversaire...
                            </p>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs text-center text-amber-400">Vérifiez les scores avant de confirmer</p>
                              <Button
                                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold text-sm uppercase transition-all shadow-lg"
                                onClick={() => confirmScoreMutation.mutate()}
                                disabled={confirmScoreMutation.isPending}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {confirmScoreMutation.isPending ? 'Confirmation...' : 'Confirmer le score'}
                              </Button>
                              <Button
                                variant="outline"
                                className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 font-bold text-sm uppercase"
                                onClick={() => disputeScoreMutation.mutate()}
                                disabled={disputeScoreMutation.isPending}>
                                {disputeScoreMutation.isPending ? 'Contestation...' : 'Contester le score'}
                              </Button>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* SCORE_DISPUTED — resubmit form or waiting view */}
                    {activeDuel && activeDuel.status === 'SCORE_DISPUTED' && (() => {
                      const isParticipant = activeDuel.attacker_user_id === user.id || activeDuel.defender_user_id === user.id;
                      if (!isParticipant) {
                        return (
                          <Card className="bg-amber-500/10 border border-amber-500/30 p-4">
                            <p className="text-amber-400 text-sm font-semibold text-center">
                              Score contesté entre les joueurs.
                            </p>
                          </Card>
                        );
                      }

                      return (
                        <>
                          <Card className="bg-red-500/10 border border-red-500/30 p-4 space-y-3">
                            <p className="text-xs uppercase tracking-wider text-red-400 font-bold text-center">
                              Score contesté
                            </p>
                            <div className="grid grid-cols-2 gap-3 text-center">
                              <div>
                                <p className="text-xs text-[#B3B6C2]">{activeDuel.attacker_name}</p>
                                <p className="text-2xl font-black text-white line-through opacity-50">{activeDuel.attacker_score ?? '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#B3B6C2]">{activeDuel.defender_name}</p>
                                <p className="text-2xl font-black text-white line-through opacity-50">{activeDuel.defender_score ?? '-'}</p>
                              </div>
                            </div>
                            <p className="text-xs text-center text-[#9CA0AB]">
                              Un joueur peut resoumettre un score corrigé. Sans action, le score précédent sera validé automatiquement.
                            </p>
                          </Card>

                          <ScoreSubmissionForm
                            duel={activeDuel}
                            user={user}
                            onSubmit={(scores) => resubmitScoreMutation.mutate(scores)}
                            isLoading={resubmitScoreMutation.isPending}
                          />
                        </>
                      );
                    })()}

                    {playground.status === 'LOCKED' && playground.owner_user_id === user.id &&
          <Button
            className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold text-sm uppercase transition-all shadow-lg hover:shadow-lg hover:shadow-[#6E2BFF]/40"
            onClick={() => cancelCooldownMutation.mutate()}
            disabled={cancelCooldownMutation.isPending}>

                            <Clock className="w-4 h-4 mr-2" />
                            {cancelCooldownMutation.isPending ? 'Annulation...' : 'Annuler le Cooldown'}
                        </Button>
          }

                    {/* Post Highlight Button */}
                    {lastCompletedDuel && 
                     lastCompletedDuel.winner_user_id === user.id && 
                     canPostHighlight?.allowed && (
                      <Button
                        className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm uppercase transition-all shadow-lg"
                        onClick={handlePostHighlight}
                        disabled={uploadingHighlight}
                      >
                        {uploadingHighlight ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Upload en cours...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Poster un highlight ({canPostHighlight.remaining_slots} restant{canPostHighlight.remaining_slots > 1 ? 's' : ''})
                          </>
                        )}
                      </Button>
                    )}
                </div> :

        <Button
          className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold text-sm uppercase transition-all shadow-lg hover:shadow-lg hover:shadow-[#6E2BFF]/40"
          onClick={() => navigate('/login')}>

                    Connexion
                </Button>
        }
            </div>

            {/* Photo Lightbox Modal */}
            {selectedPhoto &&
      <div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={() => setSelectedPhoto(null)}>

                    <div className="relative max-w-4xl max-h-[90vh]">
                        <img
            src={selectedPhoto}
            alt="Full size photo"
            className="w-full h-auto rounded-lg" />

                        <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 bg-[#6E2BFF] hover:bg-[#8B5CFF] rounded-full p-2 text-white">

                            ✕
                        </button>
                    </div>
                </div>
      }

            {/* Highlight Video Player Modal (HLS-compatible) */}
            {selectedHighlight && (
              <HighlightPlayerModal
                highlight={selectedHighlight}
                onClose={handleCloseHighlight}
              />
            )}
            </>);

}

/** Display highlight owner username */
function HighlightOwner({ userId }) {
  const { data: ownerInfo } = useQuery({
    queryKey: ['highlightOwner', userId],
    queryFn: async () => {
      try {
        const { data: userInfo } = await supabase.from('users').select('*').eq('id', userId).single();
        return userInfo || null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
    staleTime: 60000
  });

  if (!ownerInfo) return null;

  return (
    <span className="text-xs text-[#B3B6C2] truncate">
      @{ownerInfo.username || ownerInfo.full_name?.split('@')[0]}
    </span>
  );
}

/** HLS-compatible video player modal */
function HighlightPlayerModal({ highlight, onClose }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !highlight.mux_playback_id) return;

    const src = `https://stream.mux.com/${highlight.mux_playback_id}.m3u8`;

    // Safari supports HLS natively
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.play().catch(() => {});
      return;
    }

    // Other browsers: use hls.js via CDN
    let hls;
    import('https://cdn.jsdelivr.net/npm/hls.js@latest/+esm').then((mod) => {
      const Hls = mod.default;
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
      }
    }).catch(() => {
      // Last resort: set src directly and hope for the best
      video.src = src;
    });

    return () => {
      if (hls) hls.destroy();
    };
  }, [highlight.mux_playback_id]);

  return (
    <div
      className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <video
          ref={videoRef}
          controls
          playsInline
          className="w-full rounded-lg"
        />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-[#6E2BFF] hover:bg-[#8B5CFF] rounded-full p-2 text-white"
        >
          ✕
        </button>
        <div className="mt-4 text-white text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-[#B3B6C2]">
            <Eye className="w-4 h-4" />
            <span>{highlight.views_count || 0} vues</span>
          </div>
        </div>
      </div>
    </div>
  );
}